import { useState, useEffect } from "react";
import { Category, Product } from "../../pages/WareHouseMgt/types";
import {
  addProduct,
  fetchCategories,
  updateProduct,
} from "../../pages/WareHouseMgt/data";
import { BiX } from "react-icons/bi";

interface AddProductModalProps {
  storeId: string;
  product: Product | null;
  onClose: () => void;
  onProductAdded: (product: Product) => void;
}

export default function AddProductModal({
  storeId,
  product,
  onClose,
  onProductAdded,
}: AddProductModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    price: "",
    quantity: "",
    unit: "",
    buyingPrice: "",
    expiryDate: "",
    deliveryDate: "",
    image: "",
    attribute: "Regular" as Product["attribute"],
  });

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();

    if (product) {
      setFormData({
        name: product.name,
        category: product.category,
        price: product.price.toString(),
        quantity: product.quantity.toString(),
        unit: product.unit || "",
        buyingPrice: product.buyingPrice?.toString() || "",
        expiryDate: product.expiryDate || "",
        deliveryDate: product.deliveryDate || "",
        image: product.image,
        attribute: product.attribute,
      });
    }
  }, [product]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const productData = {
        name: formData.name,
        category: formData.category,
        price: parseFloat(formData.price),
        quantity: parseInt(formData.quantity),
        unit: formData.unit,
        buyingPrice: formData.buyingPrice
          ? parseFloat(formData.buyingPrice)
          : undefined,
        expiryDate: formData.expiryDate,
        deliveryDate: formData.deliveryDate,
        image: formData.image || `/placeholder.svg?height=100&width=100`,
        attribute: formData.attribute as Product["attribute"],
      };

      let newProduct;

      if (product) {
        newProduct = await updateProduct(storeId, {
          ...productData,
          id: product.id,
        });
      } else {
        newProduct = await addProduct(storeId, productData);
      }

      onProductAdded(newProduct);
    } catch (error) {
      console.error("Failed to add/update product:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {product ? "Edit Product" : "New Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <BiX size={24} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Product Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter product name"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  <option value="">Select product category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                  <option value="Vegetable">Vegetable</option>
                  <option value="Fruits">Fruits</option>
                  <option value="Grain">Grain</option>
                  <option value="Drinks">Drinks</option>
                  <option value="Dietary">Dietary</option>
                  <option value="Meat">Meat</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="price"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Order Value
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Enter order value"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="quantity"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Quantity
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="Enter product quantity"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="unit"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Unit
                </label>
                <input
                  type="text"
                  id="unit"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  placeholder="Enter product unit"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label
                  htmlFor="buyingPrice"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Buying Price
                </label>
                <input
                  type="number"
                  id="buyingPrice"
                  name="buyingPrice"
                  value={formData.buyingPrice}
                  onChange={handleChange}
                  placeholder="Enter product unit"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label
                  htmlFor="expiryDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Expiry Date
                </label>
                <input
                  type="date"
                  id="expiryDate"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label
                  htmlFor="deliveryDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Date of Delivery
                </label>
                <input
                  type="date"
                  id="deliveryDate"
                  name="deliveryDate"
                  value={formData.deliveryDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label
                  htmlFor="image"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Image
                </label>
                <input
                  type="text"
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="Choose From File"
                  className="w-full px-3 py-2 border rounded-md"
                />
                <p className="text-xs text-gray-500 mt-1">
                  In a real app, this would be a file upload field.
                </p>
              </div>

              <div>
                <label
                  htmlFor="attribute"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Attribute
                </label>
                <select
                  id="attribute"
                  name="attribute"
                  value={formData.attribute}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Top Selling">Top Selling</option>
                  <option value="Selling">Selling</option>
                  <option value="Regular">Regular</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md"
              >
                Discard
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {product ? "Update Product" : "Add Product"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
